import React, { useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import Spinner from '../../../util/spinner-shim'
import { injectIntl } from 'react-intl'

import ChartPump2, { makeColumns, makeRows } from './ChartPump2'

import AppDispatcher from '../../../dispatchers/AppDispatcher'
import ChartDataLoading from './ChartDataLoading'

const Cell = function Cell ({ value }) {
  const returnVal = (
    <td>
      {value}
    </td>
  )
  return returnVal
}

Cell.propTypes = {
  value: PropTypes.node,
}

const Row = function Row ({ data }) {
  const cells = []
  for (let i = 0; i < data.length; i += 1) {
    // ignore our tooltip
    if (i !== 1) {
      let cellValue
      if (typeof data[i] !== 'undefined' && data[i] !== null && typeof data[i].getMonth === 'function') {
        cellValue = data[i].toLocaleString()
      } else {
        cellValue = data[i]
      }
      cells.push(
        <Cell key={i} value={cellValue} />
      )
    }
  }
  return (
    <tr>
      {cells}
    </tr>
  )
}

Row.propTypes = {
  data: PropTypes.array,
}

const Table = function Table ({
  sensorData,
  device,
  formatMessage,
}) {
  if (sensorData.length < 1) {
    return (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }
  const rows = makeRows(
    sensorData,
    device,
    {
      relay: formatMessage({
        id: 'sensor.control.relayOutput',
        defaultMessage: 'Relay Output',
        description: 'Relay Output',
      }),
      run: formatMessage({
        id: 'sensor.control.runSignal',
        defaultMessage: 'Run Signal',
        description: 'Run Signal',
      }),
      pressure: formatMessage({
        id: 'sensor.pressure',
        defaultMessage: 'Pressure',
        description: 'Pressure',
      }),
    }
  )
  const rowsArr = []
  for (let i = 0; i < rows.length; i += 1) {
    rowsArr.push(
      <Row key={i} data={rows[i]} />
    )
  }
  const columns = makeColumns([
    {
      type: 'date',
      label: formatMessage({
        id: 'sensor.time.short',
        defaultMessage: 'Time',
        description: 'Time',
      }),
      role: 'domain',
    },
    {
      type: 'string',
      role: 'tooltip',
      p: { html: true },
    },
    {
      type: 'number',
      label: formatMessage({
        id: 'sensor.control.relayOutput',
        defaultMessage: 'Relay Output',
        description: 'Relay Output',
      }),
      role: 'data',
    },
    {
      type: 'number',
      label: formatMessage({
        id: 'sensor.control.runSignal',
        defaultMessage: 'Run Signal',
        description: 'Run Signal',
      }),
      role: 'data',
    },
    {
      type: 'number',
      label: formatMessage({
        id: 'sensor.pressure',
        defaultMessage: 'Pressure',
        description: 'Pressure',
      }),
      role: 'data',
    },
  ])
  const columnArr = []
  for (let i = 0; i < columns.length; i += 1) {
    columnArr.push(columns[i].label)
  }
  return (
    <table className='table table-striped'>
      <thead>
        <Row data={columnArr} />
      </thead>
      <tbody>
        {rowsArr}
      </tbody>
    </table>
  )
}
Table.propTypes = {
  sensorData: PropTypes.arrayOf(PropTypes.object),
  device: PropTypes.object,
}

function TelemetryPump ({
  device, location, periodStart, periodStop, sensorData, metricReady, intl,
}) {
  const { formatMessage } = intl

  const getNewData = useCallback((address, periodStartParam, periodStopParam) => {
    AppDispatcher.dispatch({
      action: 'get-metric-address',
      address: address || device.address,
      timeBeg: periodStartParam || periodStart,
      timeEnd: periodStopParam || periodStop,
    })
  }, [device.address, periodStart, periodStop])

  useEffect(() => {
    getNewData()
  }, [getNewData])

  useEffect(() => {
    const interval = setInterval(getNewData, 60000)
    return () => {
      clearInterval(interval)
    }
  }, [getNewData])

  useEffect(() => {
    getNewData(null, periodStart, periodStop)
  }, [periodStart, periodStop, getNewData])

  if (
    !Array.isArray(sensorData) ||
    sensorData.length < 1 ||
    !metricReady
  ) {
    return (
      <ChartDataLoading />
    )
  }
  return (
    <div className='deviceInfo'>
      <ChartPump2
        sensorData={sensorData}
        metricReady={metricReady}
        device={device}
      />
      <Table
        sensorData={sensorData}
        device={device}
        formatMessage={formatMessage}
      />
    </div>
  )
}

TelemetryPump.propTypes = {
  device: PropTypes.object,
  location: PropTypes.object,
  periodStart: PropTypes.string,
  periodStop: PropTypes.string,
  sensorData: PropTypes.array,
  metricReady: PropTypes.bool,
}

export default injectIntl(TelemetryPump)
